import {
  ENHANCEMENT_MODE,
  ENHANCEMENT_FILTERS,
  type EnhancementMode,
} from '@src/constants/enhancements.ts';
import {
  MAP_DRAW_LAYERS,
  MAP_DRAW_TOOLS,
  RightPanelsValueType,
  UI_MODES,
  UiModesValueType,
} from '@src/constants/map.ts';
import {
  MapState,
  StateSlice,
  DrawTool,
  EnhanceFilter,
} from '@src/stores/types.ts';
import {type Feature} from 'geojson';
import {type Coordinate} from 'ol/coordinate.js';
import {type Extent} from 'ol/extent';

const enhanceModeInitialState = {
  mode: ENHANCEMENT_MODE.OFF,
  // slider values,
  filters: {
    [ENHANCEMENT_FILTERS.BRIGHTNESS]: 0,
    [ENHANCEMENT_FILTERS.CONTRAST]: 0,
    [ENHANCEMENT_FILTERS.SATURATION]: 0,
  },
};

export const initialState = {
  fitExtent: [],
  locationMarkerCoordinate: null,
  bufferedExtent: null, // The current map extent/bounding box excluding ui paddings in EPSG:4326.
  extent: null, // The current map extent/bounding box in EPSG:4326.
  drawTool: {
    name: MAP_DRAW_TOOLS.NONE,
    layer: MAP_DRAW_LAYERS.NONE,
    target: '',
  },
  drawToolsVisible: true,
  feature: null, // The current map feature AOI in EPSG:4326
  isOverZoomed: false,
  centerMarkerCoord: null,
  uiMode: UI_MODES.NONE,
  rightPanel: null,
  enhanceMode: enhanceModeInitialState,
};

export const setMapSlice: StateSlice<MapState> = set => ({
  ...initialState,
  setCenterMarkerCoord: (coordinate: Coordinate | null) =>
    set(
      state => {
        state.map.centerMarkerCoord = coordinate;
      },
      undefined,
      'map/setCenterMarkerCoord',
    ),
  setDrawTool: (tool: DrawTool) =>
    set(
      state => {
        state.map.drawTool = tool;
      },
      undefined,
      'map/setDrawTool',
    ),
  setDrawToolsVisible: (visible: boolean) =>
    set(
      state => {
        state.map.drawToolsVisible = visible;
      },
      undefined,
      'map/setDrawToolsVisible',
    ),
  setEnhanceMode: (mode: EnhancementMode) =>
    set(
      state => {
        state.map.enhanceMode.mode = mode;

        state.map.enhanceMode.filters = enhanceModeInitialState.filters;
      },
      undefined,
      'map/setEnhanceMode',
    ),
  setEnhanceFilter: (filter: EnhanceFilter) =>
    set(
      state => {
        state.map.enhanceMode.filters[filter.name] = filter.value;
      },
      undefined,
      'map/setEnhanceFilter',
    ),
  setIsOverZoomed: (isOverZoomed: boolean) =>
    set(
      state => {
        state.map.isOverZoomed = isOverZoomed;
      },
      undefined,
      'map/setIsOverzoomed',
    ),
  setLocationMarkerCoordinate: (coordinate: Coordinate) =>
    set(
      state => {
        state.map.locationMarkerCoordinate = coordinate;
      },
      undefined,
      'map/setLocationMarkerCoordinate',
    ),
  setMapBufferedExtent: (extent: Extent | null) =>
    set(
      state => {
        state.map.bufferedExtent = extent;
      },
      undefined,
      'map/setMapBufferedExtent',
    ),
  setMapExtent: (extent: Extent | null) =>
    set(
      state => {
        state.map.extent = extent;
      },
      undefined,
      'map/setMapExtent',
    ),
  setMapFeature: (feature: Feature | null) =>
    set(
      state => {
        state.map.feature = feature;
      },
      undefined,
      'map/setFeature',
    ),
  setMapFitExtent: (extent: Extent | null) =>
    set(
      state => {
        state.map.fitExtent = extent;
      },
      undefined,
      'map/setMapFitExtent',
    ),
  setRightPanel: (panel: RightPanelsValueType | null) =>
    set(
      state => {
        state.map.rightPanel = panel;
      },
      undefined,
      'map/setRightPanel',
    ),

  setUIMode: (mode: UiModesValueType) =>
    set(
      state => {
        state.map.uiMode = mode;
      },
      undefined,
      'map/setUIMode',
    ),
});
